<template>
  <Menu
    v-slot="{ open }"
    as="div"
    class="relative cursor-pointer text-fg"
    :class="containerClass"
    :data-test="name"
  >
    <span v-if="syncOpen(open)" />

    <MenuButton
      ref="actionMenuButtonRef"
      :disabled="disabled"
      class="flex w-full items-center rounded border border-line py-[7px] pl-[7px] pr-3 text-ui focus:border-primary"
      :class="[
        designSystem && 'ds-input',
        open && 'border-primary text-primary',
        btnClass,
        disabled && 'bg-gray-100 opacity-80',
      ]"
    >
      <!-- pr-2, not pr-10: the caret is a flex sibling below with its own width,
           so the label never has to reserve room for it. The old 40px reservation
           was spacing counted twice, and on the narrow controls it ate the label -
           "Rows per page" is 104px wide, which left 22px for text and clipped
           "100" to "1...". -->
      <span
        class="relative block flex-1 truncate pl-0 pr-2 text-left"
        :class="[
          isValueEmpty && 'text-fg text-opacity-50',
          isValueOrphaned && 'italic text-amber-600',
          placeholderClass,
        ]"
      >
        {{ $t(getValueLabel) }}
      </span>
      <Tooltip
        v-if="isValueOrphaned"
        :description="$t('extended_field.missing_option_warning')"
        teleport
        placement="top"
        class="mr-2 shrink-0"
      >
        <FontAwesomeIcon
          :icon="['far', 'triangle-exclamation']"
          class="text-xs text-amber-600"
        />
      </Tooltip>
      <FontAwesomeIcon icon="caret-down" class="shrink-0 text-xs" />
    </MenuButton>

    <MenuItems
      v-show="open"
      ref="itemsRef"
      as="div"
      class="z-50 max-h-[150px] overflow-y-auto rounded border border-[rgba(0,0,0,.15)] bg-white"
      :class="dropdownClass"
      :style="{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }"
    >
      <input
        v-if="showSearch"
        v-model="searchText"
        type="text"
        placeholder="Search"
        class="sticky top-0 w-full border-b px-2.5 pt-2.5 pb-[7px] text-xs"
        autofocus
        @keydown.space.stop
      />

      <div
        v-if="!filteredItems.length"
        class="bg-gray-50 px-2.5 pt-2.5 pb-[7px] text-center text-xs italic text-fg text-opacity-50"
      >
        {{ $t('common.no_items') }}
      </div>

      <MenuItem
        v-if="showPlaceholder && filteredItems.length"
        v-slot="{ active }"
        as="template"
      >
        <button
          :class="[
            'block w-full text-left cursor-pointer border-b border-secondary px-2.5 pt-2.5 pb-[7px] text-xs text-fg text-opacity-50 hover:bg-surface-muted',
            active && 'bg-surface-muted!',
            placeholderClass,
          ]"
          @click="
            () => {
              searchText = ''
              emit('update:modelValue', null)
              emit('change', null)
            }
          "
        >
          {{ placeholderText ? placeholderText : $t('common.select_one') }}
        </button>
      </MenuItem>

      <MenuItem
        v-if="modelValue !== null && optional && !includesNullValue"
        v-slot="{ active }"
        as="template"
      >
        <button
          :class="[
            'block w-full text-left cursor-pointer border-b border-secondary px-2.5 pt-2.5 pb-[7px] text-xs text-fg text-opacity-50 hover:bg-surface-muted',
            active && 'bg-surface-muted!',
            placeholderClass,
          ]"
          @click="
            () => {
              searchText = ''
              emit('update:modelValue', null)
              emit('change', null)
            }
          "
        >
          {{ placeholderText ? placeholderText : $t('common.reset') }}
        </button>
      </MenuItem>

      <MenuItem
        v-for="item in filteredItems"
        :key="item[keyValue] ?? item"
        v-slot="{ active }"
        as="template"
        :disabled="isOptionDisabled(item)"
      >
        <button
          class="flex w-full items-center gap-2 text-left border-b border-secondary px-2.5 pt-2.5 pb-[7px] text-xs"
          :class="{
            'bg-gray-100 text-gray-500 cursor-not-allowed':
              isOptionDisabled(item),
            'hover:bg-surface-muted text-fg': !isOptionDisabled(item),
            'bg-surface-muted!': active,
          }"
          @click="isOptionDisabled(item) ? null : selectItem(item)"
        >
          <span class="flex-1 truncate">
            {{ keyLabel ? getNestedObjectValue(item, keyLabel) : item }}
          </span>
          <Tooltip
            v-if="isOptionDisabled(item) && item[disabledHintKey]"
            :description="item[disabledHintKey]"
            teleport
            placement="left"
            class="shrink-0"
          />
        </button>
      </MenuItem>
    </MenuItems>
  </Menu>
</template>

<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import Tooltip from '@/Components/common/Tooltip.vue'
import { useDesignSystem } from '@/composables/designSystem'

/** Design-system look: the shared input (design-system/input.css), as every other form field. */
const designSystem = useDesignSystem()

const props = defineProps({
  modelValue: {
    type: [String, Boolean, Number, Object, null],
    default: null,
  },
  value: {
    type: [String, Boolean, Number, Object, null],
    default: null,
  },
  items: {
    type: Object,
    required: true,
  },
  keyValue: {
    type: [String, null],
    default: null,
  },
  keyLabel: {
    type: [String, null],
    default: null,
  },
  btnClass: {
    type: [String, Object, Array],
    default: '',
  },
  placeholderClass: {
    type: String,
    default: '',
  },
  containerClass: {
    type: [Array, Object, String],
    default: '',
  },
  dropdownClass: {
    type: String,
    required: false,
    default: () => '',
  },
  showPlaceholder: {
    type: Boolean,
    default: false,
  },
  optional: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: '',
  },
  placeholderText: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disabledItemKey: {
    type: String,
    required: false,
    default: () => 'disabled',
  },
  disabledHintKey: {
    type: String,
    required: false,
    default: () => 'disabledHint',
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const actionMenuButtonRef = ref(null)
const itemsRef = ref(null)
const position = ref({ top: 0, left: 0, width: 0 })
const openState = ref(false)
const searchText = ref('')

const includesNullValue = computed(() =>
  props.items.some((item) =>
    props.keyValue
      ? typeof item === 'object' && item[props.keyValue] === null
      : item === null,
  ),
)

const isValueEmpty = computed(() => {
  if (props.modelValue === null) {
    return !includesNullValue.value
  }

  return [undefined, '', '0'].includes(props.modelValue)
})

const isValueOrphaned = computed(
  () => !isValueEmpty.value && selectedItem.value === undefined,
)

const filteredItems = computed(() => {
  if (!searchText.value) return props.items
  return props.items.filter((item) => {
    const label = props.keyLabel
      ? getNestedObjectValue(item, props.keyLabel)
      : item
    return label?.toLowerCase().includes(searchText.value.toLowerCase())
  })
})

const selectedItem = computed(() =>
  props.items.find((item) =>
    typeof item === 'object'
      ? item[props.keyValue] === props.modelValue
      : item === props.modelValue,
  ),
)

const orphanedValueLabel = computed(() =>
  ['string', 'number', 'boolean'].includes(typeof props.modelValue)
    ? String(props.modelValue)
    : props.placeholderText || 'common.select_one',
)

const getValueLabel = computed(() => {
  if (isValueEmpty.value) {
    return props.placeholderText || 'common.select_one'
  }

  if (props.keyLabel && selectedItem.value) {
    return (
      getNestedObjectValue(selectedItem.value, props.keyLabel) ??
      orphanedValueLabel.value
    )
  }

  return selectedItem.value ?? orphanedValueLabel.value
})

const selectItem = (item) => {
  const value = props.keyValue ? item[props.keyValue] : item
  searchText.value = ''
  emit('update:modelValue', value)
  emit('change', value)
}

const isOptionDisabled = (item) => item?.[props.disabledItemKey]

const getNestedObjectValue = (obj, path) => {
  if (!obj) return
  return path.split('.').reduce((o, k) => o?.[k], obj)
}

/* ------ dropdown positioning ------------*/
const syncOpen = (val) => (openState.value = val)

const adjustDropdown = async () => {
  await nextTick()

  const button = actionMenuButtonRef.value?.$el
  const items = itemsRef.value?.$el
  if (!button || !items) return

  const rect = button.getBoundingClientRect()
  const menuHeight = items.offsetHeight

  const spaceBelow = window.innerHeight - rect.bottom
  const placeAbove = spaceBelow < menuHeight && rect.top > menuHeight

  position.value = {
    top: placeAbove ? rect.top - menuHeight - 8 : rect.bottom + 8,
    left: rect.left,
    width: rect.width,
  }
}

watch(openState, (open) => {
  if (open) adjustDropdown()
})

onMounted(() => {
  window.addEventListener('scroll', adjustDropdown, true)
  window.addEventListener('resize', adjustDropdown)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', adjustDropdown, true)
  window.removeEventListener('resize', adjustDropdown)
})
</script>
